/**
 * Debug panel overlay for inspecting physics state.
 * Toggle with backtick (`) key. Shows live stats, export buttons, wireframe toggle.
 */

import { useEffect, useState, useCallback } from 'react';
import { useGameStore } from '../../stores/gameStore';
import { exportPhysicsSnapshot, exportTimingState, exportReconciliationLog, exportAll, exportSceneryState } from '../systems/DebugExport';
import { getReconciliationStats } from '../systems/ReconciliationLog';
import { getAsteroidSyncStats } from '../systems/AsteroidSyncLog';
import styles from './DebugPanel.module.css';

interface DebugPanelProps {
  readonly debugWireframes: boolean;
  readonly onToggleWireframes: () => void;
  readonly clientTick: number;
  readonly fps: number;
}

export function DebugPanel({ debugWireframes, onToggleWireframes, clientTick, fps }: DebugPanelProps) {
  const [visible, setVisible] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [exportingPhysics, setExportingPhysics] = useState(false);
  const [exportingTiming, setExportingTiming] = useState(false);
  const [exportingRecon, setExportingRecon] = useState(false);
  const [exportingScenery, setExportingScenery] = useState(false);

  // Toggle panel with backtick key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === '`' || e.code === 'Backquote') {
        e.preventDefault();
        setVisible((v) => !v);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleExportAll = useCallback(async () => {
    setExportingAll(true);
    try {
      await exportAll();
    } finally {
      setExportingAll(false);
    }
  }, []);

  const handleExportPhysics = useCallback(async () => {
    setExportingPhysics(true);
    try {
      await exportPhysicsSnapshot();
    } finally {
      setExportingPhysics(false);
    }
  }, []);

  const handleExportTiming = useCallback(() => {
    setExportingTiming(true);
    try {
      exportTimingState();
    } finally {
      setExportingTiming(false);
    }
  }, []);

  const handleExportRecon = useCallback(() => {
    setExportingRecon(true);
    try {
      exportReconciliationLog();
    } finally {
      setExportingRecon(false);
    }
  }, []);

  const handleExportScenery = useCallback(async () => {
    setExportingScenery(true);
    try {
      await exportSceneryState();
    } finally {
      setExportingScenery(false);
    }
  }, []);

  if (!visible) return null;

  return <DebugPanelContent
    debugWireframes={debugWireframes}
    onToggleWireframes={onToggleWireframes}
    clientTick={clientTick}
    fps={fps}
    exportingAll={exportingAll}
    onExportAll={handleExportAll}
    exportingPhysics={exportingPhysics}
    onExportPhysics={handleExportPhysics}
    exportingTiming={exportingTiming}
    onExportTiming={handleExportTiming}
    exportingRecon={exportingRecon}
    onExportRecon={handleExportRecon}
    exportingScenery={exportingScenery}
    onExportScenery={handleExportScenery}
  />;
}

interface ContentProps {
  readonly debugWireframes: boolean;
  readonly onToggleWireframes: () => void;
  readonly clientTick: number;
  readonly fps: number;
  readonly exportingAll: boolean;
  readonly onExportAll: () => void;
  readonly exportingPhysics: boolean;
  readonly onExportPhysics: () => void;
  readonly exportingTiming: boolean;
  readonly onExportTiming: () => void;
  readonly exportingRecon: boolean;
  readonly onExportRecon: () => void;
  readonly exportingScenery: boolean;
  readonly onExportScenery: () => void;
}

function DebugPanelContent({
  debugWireframes, onToggleWireframes, clientTick, fps,
  exportingAll, onExportAll,
  exportingPhysics, onExportPhysics,
  exportingTiming, onExportTiming,
  exportingRecon, onExportRecon,
  exportingScenery, onExportScenery,
}: ContentProps) {
  const serverTick = useGameStore((s) => s.serverTick);
  const moonAngle = useGameStore((s) => s.moonAngle);
  const localShip = useGameStore((s) => s.localShip);
  const pendingInputs = useGameStore((s) => s.pendingInputs);
  const connectionStatus = useGameStore((s) => s.connectionStatus);
  const desyncDistance = useGameStore((s) => s.desyncDistance);
  const liveFrameMetrics = useGameStore((s) => s.liveFrameMetrics);

  const speed = localShip
    ? Math.sqrt(localShip.vx * localShip.vx + localShip.vy * localShip.vy).toFixed(1)
    : '0.0';

  const tickDelta = clientTick - serverTick;

  // Reconciliation stats (cheap — no allocation, just iterates buffer)
  const reconStats = getReconciliationStats();
  const asteroidStats = getAsteroidSyncStats();
  const lastReconAgo = reconStats.lastEventTimestamp > 0
    ? ((performance.now() - reconStats.lastEventTimestamp) / 1000).toFixed(1)
    : '-';

  return (
    <div className={styles.panel}>
      <div className={styles.title}>Debug Panel</div>

      <div className={styles.row}>
        <span className={styles.label}>FPS</span>
        <span className={`${styles.value} ${fps < 50 ? styles.mismatch : ''}`}>{fps}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Connection</span>
        <span className={styles.value}>{connectionStatus}</span>
      </div>

      <div className={styles.divider} />

      <div className={styles.row}>
        <span className={styles.label}>Client Tick</span>
        <span className={styles.value}>{clientTick}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Server Tick</span>
        <span className={styles.value}>{serverTick}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Tick Delta</span>
        <span className={`${styles.value} ${Math.abs(tickDelta) > 10 ? styles.mismatch : ''}`}>
          {tickDelta > 0 ? '+' : ''}{tickDelta}
        </span>
      </div>

      <div className={styles.divider} />

      <div className={styles.row}>
        <span className={styles.label}>Moon Angle</span>
        <span className={styles.value}>{moonAngle.toFixed(3)}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Desync Dist</span>
        <span className={`${styles.value} ${desyncDistance > 1.0 ? styles.mismatch : ''}`}>
          {desyncDistance.toFixed(3)}
        </span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Pending Inputs</span>
        <span className={styles.value}>{pendingInputs.length}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Ship Speed</span>
        <span className={`${styles.value} ${Number(speed) > 100 ? styles.mismatch : ''}`}>
          {speed}
        </span>
      </div>
      {localShip && (
        <div className={styles.row}>
          <span className={styles.label}>Ship Pos</span>
          <span className={styles.value}>
            {localShip.x.toFixed(1)}, {localShip.y.toFixed(1)}
          </span>
        </div>
      )}

      <div className={styles.divider} />

      <div className={styles.row}>
        <span className={styles.label}>Frame Time</span>
        <span className={`${styles.value} ${liveFrameMetrics.frameTimeMs > 20 ? styles.mismatch : ''}`}>
          {liveFrameMetrics.frameTimeMs.toFixed(2)} ms
        </span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Ticks/Frame</span>
        <span className={styles.value}>{liveFrameMetrics.ticksLastFrame}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Stutters</span>
        <span className={`${styles.value} ${liveFrameMetrics.stutterCount > 0 ? styles.mismatch : ''}`}>
          {liveFrameMetrics.stutterCount}
        </span>
      </div>

      <div className={styles.divider} />

      <div className={styles.row}>
        <span className={styles.label}>Avg Desync</span>
        <span className={`${styles.value} ${reconStats.avgDesyncDistance > 1.0 ? styles.mismatch : ''}`}>
          {reconStats.avgDesyncDistance.toFixed(3)}
        </span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Last Recon</span>
        <span className={styles.value}>
          {lastReconAgo}s / {reconStats.lastDesyncDistance.toFixed(2)}
        </span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Asteroid Drift</span>
        <span className={`${styles.value} ${asteroidStats.lastMaxPositionDelta > 5.0 ? styles.mismatch : ''}`}>
          {asteroidStats.lastMaxPositionDelta.toFixed(2)}
        </span>
      </div>

      <div className={styles.divider} />

      <div className={styles.controls}>
        <button
          type="button"
          className={styles.button}
          onClick={onExportAll}
          disabled={exportingAll}
        >
          {exportingAll ? 'Exporting...' : 'Export All'}
        </button>

        <button
          type="button"
          className={styles.button}
          onClick={onExportPhysics}
          disabled={exportingPhysics}
        >
          {exportingPhysics ? 'Exporting...' : 'Export Physics'}
        </button>

        <button
          type="button"
          className={styles.button}
          onClick={onExportTiming}
          disabled={exportingTiming}
        >
          {exportingTiming ? 'Exporting...' : 'Export Timing'}
        </button>

        <button
          type="button"
          className={styles.button}
          onClick={onExportRecon}
          disabled={exportingRecon}
        >
          {exportingRecon ? 'Exporting...' : 'Export Reconciliation'}
        </button>

        <button
          type="button"
          className={styles.button}
          onClick={onExportScenery}
          disabled={exportingScenery}
        >
          {exportingScenery ? 'Exporting...' : 'Export Scenery'}
        </button>

        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={debugWireframes}
            onChange={onToggleWireframes}
          />
          Show Wireframes
        </label>
      </div>

      <div className={styles.hint}>Press ` to toggle</div>
    </div>
  );
}
